import { selectorFor } from "./interactions";
import type { CommentAnchor, CommentContext, CommentEvent } from "./types";
import type { Translate } from "./i18n";

interface Handlers {
  /** Wird bei jedem abgeschickten Kommentar aufgerufen (→ an den Relay senden). */
  onComment: (e: CommentEvent) => void;
  /** rrweb-Mirror-ID eines Knotens (>0 = gültig). */
  getNodeId: (node: Node | null) => number;
  /** Aktueller Prototyp-Zustand (für context.state). */
  getState?: () => unknown;
  /** Letzte High-Level-Schritte (für context.trail = Weg dahin). */
  getTrail?: () => { t: number; type: string; label: string }[];
  /** Übersetzer (probanden-sichtbare Texte). */
  t: Translate;
}

interface Pin {
  el: Element | null; // Anker-Element (für Reposition bei Scroll/Resize)
  fx: number;
  fy: number;
  vx: number;
  vy: number;
  n: number; // Anzeigenummer
  dot: HTMLElement; // Pin im DOM
  text: string;
  page: string; // „Seite" bei Platzierung — Pin nur dort zeigen (Seiten-Scope)
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

/**
 * „Seite" des Prototyps (für den Seiten-Scope der Pins). SPA-Views unterscheiden
 * sich i. d. R. in pathname/Query (im Demo z. B. ?view=depot). Pins einer anderen
 * Seite werden so nicht auf der aktuellen angezeigt.
 */
const pageKey = (): string => location.pathname + location.search;

/** Element aktuell tatsächlich gerendert? (entfernt oder display:none → nein). */
function isRendered(el: Element): boolean {
  if (!el.isConnected) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 || r.height > 0;
}

const STYLE = `
:host { all: initial; }
* { box-sizing: border-box; font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; }
.toggle {
  position: fixed; left: 16px; bottom: 16px; z-index: 2147483000;
  display: flex; align-items: center; gap: 8px;
  padding: 9px 14px; border-radius: 999px; border: none; cursor: pointer;
  background: #2563eb; color: #fff; font-size: 14px; font-weight: 600;
  box-shadow: 0 4px 14px rgba(0,0,0,.25);
}
.toggle.on { background: #1e40af; }
.toggle:hover { filter: brightness(1.05); }
.hint {
  position: fixed; left: 50%; bottom: 70px; transform: translateX(-50%); z-index: 2147483000;
  background: rgba(24,24,27,.92); color: #fff; padding: 7px 14px; border-radius: 999px;
  font-size: 13px; box-shadow: 0 4px 14px rgba(0,0,0,.25); pointer-events: none; white-space: nowrap;
}
/* Layer ist viewport-fixed und bildet den Bezugsrahmen; Pins/Composer/Draft sind
   absolute DARIN. So bleibt die Positionierung korrekt, selbst wenn ein Vorfahre
   ein transform/filter hat (z. B. skalierte Prototyp-Viewports) — dann wäre
   position:fixed sonst relativ zum transformierten Vorfahren statt zum Viewport. */
.layer { position: fixed; inset: 0; z-index: 2147482000; pointer-events: none; }
.draft {
  position: absolute; width: 26px; height: 26px; margin: -26px 0 0 0; z-index: 2147483001;
  pointer-events: none; transform: translateX(-2px);
}
.pin {
  position: absolute; width: 26px; height: 26px; margin: -26px 0 0 0; z-index: 2147482500;
  pointer-events: auto; cursor: pointer;
  border-radius: 50% 50% 50% 2px; background: #2563eb; color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; border: 2px solid #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,.3);
}
.pin .bubble {
  display: none; position: absolute; left: 30px; top: -4px; width: max-content; max-width: 240px;
  background: #18181b; color: #fff; padding: 8px 10px; border-radius: 8px; font-size: 13px;
  font-weight: 400; line-height: 1.4; box-shadow: 0 4px 14px rgba(0,0,0,.3); white-space: normal;
}
.pin:hover .bubble, .pin.open .bubble { display: block; }
.composer {
  position: absolute; z-index: 2147483002; pointer-events: auto;
  display: flex; align-items: flex-end; gap: 6px;
  background: rgba(24,24,27,.94); padding: 8px; border-radius: 12px;
  box-shadow: 0 6px 20px rgba(0,0,0,.35); width: 280px;
}
.composer textarea {
  flex: 1; resize: none; border: none; outline: none; background: transparent; color: #fff;
  font-size: 14px; line-height: 1.4; min-height: 22px; max-height: 120px;
}
.composer textarea::placeholder { color: #a1a1aa; }
.composer .send {
  flex: 0 0 auto; width: 30px; height: 30px; border-radius: 50%; border: none; cursor: pointer;
  background: #3f3f46; color: #fff; font-size: 15px;
}
.composer .send:hover { background: #52525b; }
`;

/**
 * Hängt das Figma-artige Element-Feedback an: ein isoliertes Shadow-DOM-Overlay mit
 * Toggle, „in der Hand“ folgendem Draft-Pin (Drag&Drop-Gefühl), Composer und persistenten
 * Pins, die ihrem Anker-Element bei Scroll/Resize folgen. Gibt eine Cleanup-Funktion zurück.
 */
export function attachComments({ onComment, getNodeId, getState, getTrail, t }: Handlers): () => void {
  // Host bewusst mit rr-block: rrweb schneidet die Feedback-UI nicht mit
  // (sonst erschiene sie zusätzlich zum separat gerenderten Pin im Replay).
  const host = document.createElement("div");
  host.className = "rr-block rr-ignore";
  host.setAttribute("data-ul-comments", "");
  document.body.appendChild(host);
  const root = host.attachShadow({ mode: "open" });

  const style = document.createElement("style");
  style.textContent = STYLE;
  root.appendChild(style);

  const toggle = document.createElement("button");
  toggle.className = "toggle";
  toggle.innerHTML = `<span>💬</span><span></span>`;
  toggle.lastElementChild!.textContent = t("comments.feedback");
  root.appendChild(toggle);

  const layer = document.createElement("div");
  layer.className = "layer";
  root.appendChild(layer);

  let active = false;
  let draft: HTMLElement | null = null;
  let hint: HTMLElement | null = null;
  let composer: HTMLElement | null = null;
  // Anker des offenen Composers, damit er beim Scrollen am Element bleibt (wie ein Pin).
  let composerEl: Element | null = null;
  let composerFx = 0.5;
  let composerFy = 0.5;
  const pins: Pin[] = [];
  let counter = 0;

  // ── Platzierungs-Modus an/aus ────────────────────────────────────────
  function setActive(on: boolean): void {
    active = on;
    toggle.classList.toggle("on", on);
    document.documentElement.style.cursor = on ? "crosshair" : "";
    if (on) {
      hint = document.createElement("div");
      hint.className = "hint";
      hint.textContent = t("comments.hint");
      root.appendChild(hint);
      draft = document.createElement("div");
      draft.className = "draft pin";
      draft.textContent = "+";
      draft.style.left = "-100px";
      layer.appendChild(draft);
    } else {
      hint?.remove();
      hint = null;
      draft?.remove();
      draft = null;
    }
  }

  // Element unter (x,y) ermitteln, ohne dass das Overlay selbst getroffen wird.
  function elementUnder(x: number, y: number): Element | null {
    const prev = host.style.pointerEvents;
    host.style.pointerEvents = "none";
    const el = document.elementFromPoint(x, y);
    host.style.pointerEvents = prev;
    return el && el !== host ? el : null;
  }

  function anchorFor(x: number, y: number): { el: Element | null; anchor: CommentAnchor } {
    const el = elementUnder(x, y);
    const vx = clamp01(x / Math.max(1, window.innerWidth));
    const vy = clamp01(y / Math.max(1, window.innerHeight));
    if (!el) return { el: null, anchor: { fx: 0.5, fy: 0.5, vx, vy } };
    const r = el.getBoundingClientRect();
    const fx = r.width > 0 ? clamp01((x - r.left) / r.width) : 0.5;
    const fy = r.height > 0 ? clamp01((y - r.top) / r.height) : 0.5;
    const nodeId = getNodeId(el);
    return {
      el,
      anchor: {
        nodeId: nodeId > 0 ? nodeId : undefined,
        selector: selectorFor(el) || undefined,
        fx,
        fy,
        vx,
        vy,
      },
    };
  }

  // ── Composer öffnen ──────────────────────────────────────────────────
  function openComposer(x: number, y: number, el: Element | null, anchor: CommentAnchor): void {
    closeComposer();
    const box = document.createElement("div");
    box.className = "composer";
    const ta = document.createElement("textarea");
    ta.placeholder = t("comments.placeholder");
    ta.rows = 1;
    const send = document.createElement("button");
    send.className = "send";
    send.innerHTML = "↑";
    box.appendChild(ta);
    box.appendChild(send);
    layer.appendChild(box);
    composer = box;
    composerEl = el;
    composerFx = anchor.fx;
    composerFy = anchor.fy;
    position(box, x, y);
    ta.focus();

    const autoGrow = () => {
      ta.style.height = "auto";
      ta.style.height = `${Math.min(120, ta.scrollHeight)}px`;
    };
    ta.addEventListener("input", autoGrow);

    const submit = () => {
      const text = ta.value.trim();
      if (!text) {
        closeComposer();
        return;
      }
      // Vollen Kontext einsammeln, damit der Kommentar nachvollziehbar bleibt:
      // Seite, Zustand, Weg dahin (Trail) und Inhalt (Textauszug des Elements).
      const excerpt = el ? (el.textContent || "").trim().replace(/\s+/g, " ").slice(0, 160) : "";
      const context: CommentContext = {
        url: location.href,
        path: location.pathname + location.search,
        title: document.title,
        scroll: { x: Math.round(window.scrollX), y: Math.round(window.scrollY) },
        viewport: { w: window.innerWidth, h: window.innerHeight },
        state: getState?.(),
        trail: getTrail?.(),
        contentExcerpt: excerpt || undefined,
      };
      const ev: CommentEvent = { t: Date.now(), author: "participant", text, anchor, context };
      onComment(ev);
      addPin(el, anchor, text);
      closeComposer();
    };
    send.addEventListener("click", submit);
    ta.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        submit();
      } else if (e.key === "Escape") {
        closeComposer();
      }
    });
  }

  function closeComposer(): void {
    composer?.remove();
    composer = null;
    composerEl = null;
  }

  // ── Persistenten Pin anlegen + positionieren ─────────────────────────
  function addPin(el: Element | null, anchor: CommentAnchor, text: string): void {
    const dot = document.createElement("div");
    dot.className = "pin";
    counter += 1;
    dot.textContent = String(counter);
    const bubble = document.createElement("div");
    bubble.className = "bubble";
    bubble.textContent = text;
    dot.appendChild(bubble);
    dot.addEventListener("click", (e) => {
      e.stopPropagation();
      dot.classList.toggle("open");
    });
    layer.appendChild(dot);
    const pin: Pin = {
      el,
      fx: anchor.fx,
      fy: anchor.fy,
      vx: anchor.vx ?? 0.5,
      vy: anchor.vy ?? 0.5,
      n: counter,
      dot,
      text,
      page: pageKey(),
    };
    pins.push(pin);
    placePin(pin);
  }

  function placePin(p: Pin): void {
    let show = false;
    let x = 0;
    let y = 0;
    if (p.el) {
      // Element-verankert: nur zeigen, solange das Element wirklich gerendert ist.
      // Beim Seitenwechsel ist es entfernt oder display:none → Pin ausblenden
      // (kein Viewport-Fallback, sonst würde der Pin auf der Folgeseite „kleben").
      if (isRendered(p.el)) {
        const r = p.el.getBoundingClientRect();
        x = r.left + p.fx * r.width;
        y = r.top + p.fy * r.height;
        show = true;
      }
    } else if (p.page === pageKey()) {
      // Viewport-verankert (Klick auf leere Fläche): nur auf der Erstellungs-Seite.
      x = p.vx * window.innerWidth;
      y = p.vy * window.innerHeight;
      show = true;
    }
    p.dot.style.display = show ? "flex" : "none";
    if (show) position(p.dot, x, y);
  }

  // x/y sind Viewport-Koordinaten (aus getBoundingClientRect bzw. clientX/Y). Da Pins
  // absolute IM Layer liegen, in Layer-lokale Koordinaten umrechnen: Layer-Offset
  // abziehen. Ist der Layer echt viewport-fixed, ist der Offset {0,0} (keine Änderung);
  // bei transformiertem Vorfahren kompensiert er korrekt.
  function position(el: HTMLElement, x: number, y: number): void {
    const lr = layer.getBoundingClientRect();
    el.style.left = `${Math.round(x - lr.left)}px`;
    el.style.top = `${Math.round(y - lr.top)}px`;
  }

  // ── Pins kontinuierlich nachführen (Scroll/Resize/SPA-Navigation) ────
  // Eine laufende rAF-Schleife (statt nur Scroll/Resize-Events), damit Pins auch
  // bei View-Wechseln ohne Scroll-/Resize-Event sofort ihrer Sichtbarkeit folgen
  // — z. B. wenn eine SPA per pushState die Ansicht wechselt (Seiten-Scope).
  let raf = 0;
  const tick = () => {
    raf = requestAnimationFrame(tick);
    for (const p of pins) placePin(p);
    // Offenen Composer am Element halten (folgt Scroll/Resize wie ein Pin).
    if (composer && composerEl && isRendered(composerEl)) {
      const r = composerEl.getBoundingClientRect();
      position(composer, r.left + composerFx * r.width, r.top + composerFy * r.height);
    }
  };
  raf = requestAnimationFrame(tick);

  // ── Events ───────────────────────────────────────────────────────────
  const onToggle = () => {
    if (composer) closeComposer();
    setActive(!active);
  };
  const onMove = (e: MouseEvent) => {
    if (active && draft) position(draft, e.clientX, e.clientY);
  };
  const onDrop = (e: MouseEvent) => {
    if (!active) return;
    // Klicks auf die eigene UI (Toggle) nicht als Platzierung werten.
    const path = e.composedPath();
    if (path.includes(toggle)) return;
    e.preventDefault();
    e.stopPropagation();
    const { el, anchor } = anchorFor(e.clientX, e.clientY);
    setActive(false);
    openComposer(e.clientX, e.clientY, el, anchor);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape" && active) setActive(false);
  };

  toggle.addEventListener("click", onToggle);
  document.addEventListener("mousemove", onMove, true);
  // capture=true: Drop abfangen, bevor der Prototyp den Klick verarbeitet.
  document.addEventListener("click", onDrop, true);
  document.addEventListener("keydown", onKey, true);

  return () => {
    cancelAnimationFrame(raf);
    toggle.removeEventListener("click", onToggle);
    document.removeEventListener("mousemove", onMove, true);
    document.removeEventListener("click", onDrop, true);
    document.removeEventListener("keydown", onKey, true);
    document.documentElement.style.cursor = "";
    host.remove();
  };
}
